import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { login as loginRequest, me, logoutRequest } from '../api/auth';
import { apiClient, ensureCsrfToken, registerSessionExpiredHandler } from '../api/client';

interface User {
  id: number;
  email: string;
  name?: string;
  isAdmin?: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string, turnstileToken?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(async () => {
    try { await logoutRequest(); } catch { /* ignore */ }
    setUser(null);
  }, []);

  useEffect(() => {
    registerSessionExpiredHandler(() => {
      window.location.href = '/login?reason=session_expired';
    });
  }, []);

  // Warm up the CSRF token on mount and restore session.
  useEffect(() => {
    ensureCsrfToken()
      .catch(() => undefined)
      .then(() =>
        me()
          .then((res) => {
            const nextUser = res.data.data.user;
            setUser(nextUser?.isAdmin ? nextUser : null);
          })
          .catch(() => setUser(null))
          .finally(() => setIsLoading(false)),
      );
  }, []);

  const login = useCallback(async (email: string, password: string, turnstileToken?: string) => {
    const res = await loginRequest(email, password, turnstileToken);
    const { user: nextUser } = res.data.data;
    if (!nextUser?.isAdmin) {
      throw new Error('This account does not have admin access.');
    }
    await ensureCsrfToken(true).catch(() => undefined); // bind token to new session
    setUser(nextUser);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
